import React, { useEffect, useMemo, useState } from 'react';

import { toast } from "react-toastify";
import { duplicateConfig } from '../../../config/agentic/duplicateConfig';
import {
  extractDuplicateComparableFields,
  getFieldMatchLevel,
  getReportTitle,
  mapInputRecordToPrimaryRow,
  mapApiDuplicatesToTableRows,
} from '../../../utils/helper';
import DuplicateSection from '../DuplicateSection';
import EmptyBenefitsState from '../EmptyBenefitsState.jsx';
import ReportLegend from '../ReportLegend';
import ReportSummary from '../ReportSummary';
import { updateDobStatus } from '../../../api/agentic/dobStatusApi.js';

const BenefitsAgentReport = React.memo(({ reportData }) => {
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);

  const [primaryApplications, setPrimaryApplications] = useState([]);
  const [reportConfig, setReportConfig] = useState({
    legendTitle: '',
    columns: duplicateConfig.getColumns({ isBenefitsAgent: false, showAction: true }),
  });

  const [benefitsData, setBenefitsData] = useState({
    SBA: { related: [], unrelated: [] },
    FEMA: { related: [], unrelated: [] },
    NFIP: { related: [], unrelated: [] },
  });

  const comparedDuplicates = useMemo(() => {
    const primary = primaryApplications[0];
    if (!primary) return potentialDuplicates;

    return potentialDuplicates.map((row) => ({
      ...row,
      _compare: {
        firstName: getFieldMatchLevel(primary.firstName, row.firstName),
        lastName: getFieldMatchLevel(primary.lastName, row.lastName),
      },
    }));
  }, [potentialDuplicates, primaryApplications]);

  const duplicateComparable = useMemo(
    () => extractDuplicateComparableFields(potentialDuplicates),
    [potentialDuplicates],
  );

  useEffect(() => {
    const title = getReportTitle({ reportType: "BENEFITS", reportData });

    setReportConfig((prev) => ({
      ...prev,
      legendTitle: title,
    }));
  }, [reportData]);
  useEffect(() => {
    if (!reportData) return;

    const matches = reportData?.matches
      || reportData?.output_text?.matches
      || reportData?.output_text?.data_sources_matches || [];
    setPotentialDuplicates(
      mapApiDuplicatesToTableRows(matches),
    );
  }, [reportData]);

  useEffect(() => {
    const record = reportData?.input_record;

    if (!record) {
      setPrimaryApplications([]);
      return;
    }

    const mappedPrimary = mapInputRecordToPrimaryRow(record, duplicateComparable);

    setPrimaryApplications([{
      ...mappedPrimary,
      origin: mappedPrimary.origin === '-' ? 'Benefits' : mappedPrimary.origin,
      notes: 'Primary application inferred from Benefits match',
    }]);
  }, [reportData?.input_record, duplicateComparable]);

  const benefitsColumns = duplicateConfig.getColumns({
    isBenefitsAgent: true,
    showAction: false,
  });

  const handleMarkRelated = async (selectedRows, { status, source }) => {
    try {
      // Update backend
      await Promise.all(
        selectedRows.map((row) => updateDobStatus ({
          matchedCaseId: row.caseId,
          sourceCaseId: reportData.input_record.CaseID,
          status,
        })),
      );

      setBenefitsData((prev) => {
        const prevSource = prev[source];
        let related = [...prevSource.related];
        let unrelated = [...prevSource.unrelated];

        if (status === "RELATED") {
          // Move selected rows from related → unrelated
          selectedRows.forEach((row) => {
            // Remove from related if exists
            unrelated = unrelated.filter((r) => r.caseId !== row.caseId);
            // Add to unrelated if not already there
            if (!unrelated.some((r) => r.caseId === row.caseId)) {
              related.push(row);
            }
          });
        } else if (status === "UNRELATED") {
          // Move selected rows from unrelated → related
          selectedRows.forEach((row) => {
            // Remove from unrelated if exists
            related = related.filter((r) => r.caseId !== row.caseId);
            // Add to related if not already there
            if (!related.some((r) => r.caseId === row.caseId)) {
              unrelated.push(row);
            }
          });
        }

        return { ...prev, [source]: { related, unrelated } };
      });

      toast.success(`Marked as ${status.toLowerCase()}`);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to mark as ${status}`);
    }
  };

  useEffect(() => {

    const caseStatusMap = reportData?.outputText?.case_status || reportData?.case_status || {};

    const buckets = {
      SBA: { related: [], unrelated: [] },
      FEMA: { related: [], unrelated: [] },
      NFIP: { related: [], unrelated: [] },
    };

    comparedDuplicates.forEach((row) => {
      const status = caseStatusMap[row.caseId];
      if (!status) return;

      const statusKey = status.toLowerCase(); // 'related' | 'unrelated'

      if (row.dataSource === "SBA_HOME" || row.dataSource === "SBA_BUSINESS") {
        buckets.SBA[statusKey]?.push(row);
      }
      if (row.dataSource === "FEMA") {
        buckets.FEMA[statusKey]?.push(row);
      }
      if (row.dataSource === "NFIP") {
        buckets.NFIP[statusKey]?.push(row);
      }
    });

    const shallowEqual = (arr1, arr2) => arr1?.length === arr2?.length && arr1?.every((item, i) => item === arr2[i]);

    setBenefitsData((prev) => {
      const sbaChanged = !shallowEqual(prev.SBA?.related, buckets.SBA?.related)
        || !shallowEqual(prev.SBA?.unrelated, buckets.SBA?.unrelated);

      const femaChanged = !shallowEqual(prev.FEMA?.related, buckets.FEMA?.related)
        || !shallowEqual(prev.FEMA?.unrelated, buckets.FEMA?.unrelated);

      const nfipChanged = !shallowEqual(prev.NFIP?.related, buckets.NFIP?.related)
        || !shallowEqual(prev.NFIP?.unrelated, buckets.NFIP?.unrelated);

      if (!sbaChanged && !femaChanged && !nfipChanged) return prev;

      return buckets;
    });
  }, [reportData, comparedDuplicates]);

  return (
    reportData && (
    <>
      <ReportLegend title={reportConfig.legendTitle} legends />

      <ReportSummary summary={reportData?.summary} />

      <DuplicateSection
        title="Application Details"
        columns={duplicateConfig.primaryApplColumns}
        data={primaryApplications}
        showCheckbox={false}
      />

      <DuplicateSection
        title="Potential SBA Benefits"
        columns={benefitsColumns}
        data={benefitsData?.SBA?.unrelated}
        showActions
        actionLabel="Mark as Related"
        onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "RELATED", source: "SBA" })}
        emptyComponent={(
          <EmptyBenefitsState
            title="No Potential SBA Benefits"
          />
        )}
      />

      {benefitsData.SBA?.related?.length > 0 && (
        <DuplicateSection
          title="Confirmed SBA Benefits"
          columns={benefitsColumns}
          showActions
          data={benefitsData.SBA?.related}
          actionLabel="Mark as Unrelated"
          onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "UNRELATED", source: "SBA" })}
        />
      )}

        <DuplicateSection
          title="Potential FEMA Benefits"
          columns={benefitsColumns}
          data={benefitsData.FEMA?.unrelated}
          showActions
          actionLabel="Mark as Related"
          onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "RELATED", source: "FEMA" })}
          emptyComponent={(
            <EmptyBenefitsState
              title="No Potential FEMA Benefits"
            />
          )}
        />

      {benefitsData.FEMA?.related?.length > 0 && (
        <DuplicateSection
          title="Confirmed FEMA Benefits"
          columns={benefitsColumns}
          data={benefitsData.FEMA?.related}
          showActions
          actionLabel="Mark as Unrelated"
          onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "UNRELATED", source: "FEMA" })}
        />
      )}

      <DuplicateSection
        title="Potential NFIP Benefits"
        columns={benefitsColumns}
        data={benefitsData.NFIP?.unrelated}
        showActions
        actionLabel="Mark as Related"
        onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "RELATED", source: "NFIP" })}
        emptyComponent={(
          <EmptyBenefitsState
            title="No Potential NFIP Benefits"
          />
        )}
      />

      {benefitsData.NFIP?.related.length > 0 && (
        <DuplicateSection
          title="Confirmed NFIP Benefits"
          columns={benefitsColumns}
          data={benefitsData.NFIP?.related}
          actionLabel="Mark as Unrelated"
          showActions
          onMarkDuplicate={(rows) => handleMarkRelated(rows, { status: "UNRELATED", source: "NFIP" })}
        />
      )}
    </>
    )
  )

});


export default BenefitsAgentReport;